// Modal sheet mounts for the detail view (P1-18). Lifted out of
// ItemDetail.tsx so that file stays under the 400-line guard — one place
// for tag override + per-kind attribute sheets to live. Each sheet is a
// pass-through: open/closed is driven entirely by the parent's state;
// a saved mutation bubbles the updated block back up.

import type {
  AmmoAttributes,
  HolsterAttributes,
  ItemDetail,
  OpticsAttributes,
} from "../../data/catalog.ts";
import { AmmoAttrsSheet } from "./AmmoAttrsSheet.tsx";
import { HolsterAttrsSheet } from "./HolsterAttrsSheet.tsx";
import { OpticsAttrsSheet } from "./OpticsAttrsSheet.tsx";
import { TagOverrideSheet } from "./TagOverrideSheet.tsx";

type Props = {
  item: ItemDetail["item"];
  formulaPreview: string;
  tagSheetOpen: boolean;
  onTagSaved: (next: { tagNameOverride: string | null; version: number }) => void;
  onTagCancel: () => void;
  ammoAttributes: AmmoAttributes | null;
  ammoSheetOpen: boolean;
  onAmmoSaved: (next: AmmoAttributes) => void;
  onAmmoCancel: () => void;
  opticsAttributes: OpticsAttributes | null;
  opticsSheetOpen: boolean;
  onOpticsSaved: (next: OpticsAttributes) => void;
  onOpticsCancel: () => void;
  holsterAttributes: HolsterAttributes | null;
  holsterSheetOpen: boolean;
  onHolsterSaved: (next: HolsterAttributes) => void;
  onHolsterCancel: () => void;
};

export function ItemDetailSheets(props: Props) {
  const { item } = props;
  return (
    <>
      {props.tagSheetOpen && (
        <TagOverrideSheet
          itemId={item.id}
          expectedVersion={item.version}
          currentOverride={item.tagNameOverride ?? null}
          formulaPreview={props.formulaPreview}
          onCancel={props.onTagCancel}
          onSaved={(next) => props.onTagSaved(next)}
        />
      )}
      {props.ammoSheetOpen && props.ammoAttributes && (
        <AmmoAttrsSheet
          itemId={item.id}
          current={props.ammoAttributes}
          onCancel={props.onAmmoCancel}
          onSaved={(next) =>
            props.onAmmoSaved({
              manufacturer: next.manufacturer,
              caliberGauge: next.caliberGauge,
              grain: next.grain,
              bulletType: next.bulletType,
              isHollowPoint: next.isHollowPoint,
              roundsPerBox: next.roundsPerBox,
              muzzleVelocityFps: next.muzzleVelocityFps,
              version: next.version,
            })
          }
        />
      )}
      {props.opticsSheetOpen && props.opticsAttributes && (
        <OpticsAttrsSheet
          itemId={item.id}
          current={props.opticsAttributes}
          onCancel={props.onOpticsCancel}
          onSaved={(next) =>
            props.onOpticsSaved({
              manufacturer: next.manufacturer,
              model: next.model,
              magnification: next.magnification,
              reticle: next.reticle,
              mount: next.mount,
              isIlluminated: next.isIlluminated,
              batteryType: next.batteryType,
              version: next.version,
            })
          }
        />
      )}
      {props.holsterSheetOpen && props.holsterAttributes && (
        <HolsterAttrsSheet
          itemId={item.id}
          current={props.holsterAttributes}
          onCancel={props.onHolsterCancel}
          onSaved={(next) =>
            props.onHolsterSaved({
              manufacturer: next.manufacturer,
              fitModel: next.fitModel,
              mountType: next.mountType,
              drawSide: next.drawSide,
              retentionLevel: next.retentionLevel,
              material: next.material,
              version: next.version,
            })
          }
        />
      )}
    </>
  );
}
